/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the partner form
 */
class PartnerUrlForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            partnerUrlId: null,
            url: '',
            // partner dropdown
            partnerId:'',
            partnerName: '',
            // partner url type dropdown
            partnerUrlTypeId: '',
            partnerUrlTypeName: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'textUrl',
                    labelName: 'Url *',
                    inputName: 'url',
                    placeholder: 'Please enter partner url here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Partner *',
                    inputName: 'partnerId',
                    optionValueName: 'partnerId',
                    optionLabelName: 'partnerName',
                    required: true,
                    path: '/partner-urls',
                    valueType: 'partners'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Partner Url Type *',
                    inputName: 'partnerUrlTypeId',
                    optionValueName: 'partnerUrlTypeId',
                    optionLabelName: 'partnerUrlTypeName',
                    required: true,
                    path: '/partner-urls',
                    valueType: 'partnerUrlTypes'
                }
            ]
        };

        // get the parameter from URL
        const partnerUrlId = this.props.match.params.id;

        // set icons to the header
        if (partnerUrlId === 'create') {
            EventEmitter.emit('setPageHeaderData', {
                page: 'partnerUrls',
                pageName: "Partner Urls",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/partnerUrls",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'partnerUrls',
                pageName: "Partner Urls",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/partnerUrls",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deletePartnerUrl);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const partnerUrlId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create partner
        if (partnerUrlId !== 'create') {

            const url = '/partner-urls?search=%7BpartnerUrlId:' + partnerUrlId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    oldState.partnerUrlId = (data.partnerUrlId !== undefined) ? data.partnerUrlId : null;
                    oldState.url = (data.url !== undefined) ? data.url : '';
                    oldState.partnerId = (data.partnerId !== undefined) ? data.partnerId : '';
                    oldState.partnerName = (data.partnerName !== undefined) ? data.partnerName : '';
                    oldState.partnerUrlTypeId = (data.partnerUrlTypeId !== undefined) ? data.partnerUrlTypeId : '';
                    oldState.partnerUrlTypeName = (data.partnerUrlTypeName !== undefined) ? data.partnerUrlTypeName : '';
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                })
                .catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deletePartnerUrl);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel partner url async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.partnerUrlId !== null) {
            data = {
                partnerUrlId: this.state.partnerUrlId,
                url: this.state.url,
                partnerId: Number(this.state.partnerId),
                partnerUrlTypeId: Number(this.state.partnerUrlTypeId)
            };
        } else {
            data = {
                url: this.state.url,
                partnerId: Number(this.state.partnerId),
                partnerUrlTypeId: Number(this.state.partnerUrlTypeId)
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.partnerUrlId !== null) {
                this.updatePartnerUrl(data);
            } else {
                this.createPartnerUrl(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'url':
                if (value === '')
                    error[key] = "URL is Required!";
                else if (value === ' ')
                    error[key] = "URL can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "URL can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "Disabled URL must begin with with http://, https:// or / ";
                break;
            case 'partnerId':
                if (this.state.partnerId === '')
                    error[key] = "Please select a Partner!";
                else
                    error[key] = "";
                break;
            case 'partnerUrlTypeId':
                if (this.state.partnerUrlTypeId === '')
                    error[key] = "Please select a Partner URL Type!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create partner url
     * @param data - the data to create a new partner url
     */
    createPartnerUrl = async (data) => {
        await API.post('/partner-urls',
            {
                "url": data.url,
                "partnerId": data.partnerId,
                "partnerUrlTypeId": data.partnerUrlTypeId
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Partner Url");
            this.props.history.push('/home/partnerUrls');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update partner url
     * @param data - updated partner url details
     */
    updatePartnerUrl = async (data) => {
        await API.put('/partner-urls',
            {
                "partnerUrlId": data.partnerUrlId,
                "url": data.url,
                "partnerId": data.partnerId,
                "partnerUrlTypeId": data.partnerUrlTypeId
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Partner Url");
            this.props.history.push('/home/partnerUrls');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.partnerId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'partnerUrls',
                name: 'this Partner Url'
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Partner Url",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a partner
     * @param data
     */
    deletePartnerUrl = async (data) => {
        if (data && (this.props.match.path === "/home/partnerUrls/:id")) {
            await API.delete('/partner-urls?partnerUrlId=' + this.state.partnerUrlId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the Partner Url");
                    this.props.history.push('/home/partnerUrls');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Partner Url is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.url = "";
        newState.partnerId = "";
        newState.partnerName = "";
        newState.partnerUrlTypeId = "";
        newState.partnerUrlTypeName = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/partnerUrls');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default PartnerUrlForm;
